/**
 * FATIGUE INDEX SYSTEM
 *
 * Dynamic algorithm estimating accumulated fatigue based on:
 *   - intensity (RPE / %1RM)
 *   - frequency  (how often each muscle is trained per week)
 *   - movement overlap (same pattern on consecutive days)
 *   - heavy compound stacking (multiple RPE ≥ 8.5 compounds in one session)
 *   - insufficient rest days
 *
 * Output: 0–100 fatigue score, per-muscle fatigue, warnings, suggestions.
 */

import type {
  Week,
  Session,
  ExerciseInstance,
  MuscleGroup,
  FatigueMetrics,
  FatigueWarning,
  MovementPattern,
} from '../types';
import { ALL_MUSCLE_GROUPS, MUSCLE_GROUP_LABELS, MOVEMENT_PATTERN_LABELS } from '../types';
import { parseReps, classifyIntensity } from '../utils/helpers';
import { DAYS } from '../utils/helpers';

// ── Weight constants ────────────────────────

const BASE_FATIGUE_PER_SET = 1.0;
const RPE_FATIGUE_SCALE: Record<string, number> = {
  low: 0.5,
  moderate: 1.0,
  high: 1.6,
  max: 2.4,
};
const COMPOUND_MULTIPLIER = 1.3;
const FREQUENCY_PENALTY_THRESHOLD = 3; // training same muscle >3x/week adds penalty
const HEAVY_COMPOUND_THRESHOLD = 3; // ≥3 heavy compounds in one session → stacking penalty
const MIN_REST_DAYS_PER_WEEK = 1;

// ── Main fatigue calculator ─────────────────

export function calculateFatigueMetrics(week: Week, fatigueTolerance: number): FatigueMetrics {
  const warnings: FatigueWarning[] = [];
  const suggestions: string[] = [];

  // ── Per-muscle fatigue accumulator ──
  const muscleFatigue: Partial<Record<MuscleGroup, number>> = {};
  for (const m of ALL_MUSCLE_GROUPS) muscleFatigue[m] = 0;

  // ── Per-muscle frequency (how many sessions hit this muscle) ──
  const muscleFreq: Record<string, number> = {};
  for (const m of ALL_MUSCLE_GROUPS) muscleFreq[m] = 0;

  // ── Movement pattern per day (for overlap detection) ──
  const dayPatterns: Map<string, Set<MovementPattern>> = new Map();

  // ── Heavy compounds per session ──
  let totalHeavyStackingSessions = 0;

  for (const session of week.sessions) {
    const musclesHitThisSession = new Set<MuscleGroup>();
    let heavyCompoundsInSession = 0;

    // Track patterns per day
    if (!dayPatterns.has(session.day)) {
      dayPatterns.set(session.day, new Set());
    }

    for (const ex of session.exercises) {
      const rpe = ex.loadType === 'rpe' ? ex.load : ex.load / 10;
      const intClass = classifyIntensity(rpe);
      const fatiguePer = BASE_FATIGUE_PER_SET * RPE_FATIGUE_SCALE[intClass];
      const multiplier = ex.tag === 'compound' ? COMPOUND_MULTIPLIER : 1.0;
      const exFatigue = ex.sets * fatiguePer * multiplier;

      // Primary muscle
      muscleFatigue[ex.primaryMuscle] = (muscleFatigue[ex.primaryMuscle] || 0) + exFatigue;
      musclesHitThisSession.add(ex.primaryMuscle);

      // Secondary muscles (partial)
      for (const sec of ex.secondaryMuscles) {
        muscleFatigue[sec] = (muscleFatigue[sec] || 0) + exFatigue * 0.4;
        musclesHitThisSession.add(sec);
      }

      // Track pattern
      dayPatterns.get(session.day)!.add(ex.movementPattern);

      // Heavy compound detection
      if (ex.tag === 'compound' && rpe >= 8.5) {
        heavyCompoundsInSession++;
      }
    }

    // Update muscle frequency
    for (const m of musclesHitThisSession) {
      muscleFreq[m] = (muscleFreq[m] || 0) + 1;
    }

    if (heavyCompoundsInSession >= HEAVY_COMPOUND_THRESHOLD) {
      totalHeavyStackingSessions++;
      warnings.push({
        severity: 'warning',
        message: `${session.day} (${session.focus}) ma ${heavyCompoundsInSession} ciężkich ćwiczeń złożonych - rozważ redystrybucję intensywności.`,
      });
    }
  }

  // ── Frequency penalty ──
  for (const m of ALL_MUSCLE_GROUPS) {
    if (muscleFreq[m] > FREQUENCY_PENALTY_THRESHOLD) {
      const penalty = (muscleFreq[m] - FREQUENCY_PENALTY_THRESHOLD) * 3;
      muscleFatigue[m] = (muscleFatigue[m] || 0) + penalty;
      warnings.push({
        severity: 'info',
        message: `${MUSCLE_GROUP_LABELS[m]} jest trenowany ${muscleFreq[m]}× w tym tygodniu - częstotliwość może przekraczać zdolność regeneracji.`,
        muscle: m,
      });
    }
  }

  // ── Movement overlap detection (consecutive days) ──
  const dayOrder = DAYS as readonly string[];
  const sessionDays = week.sessions.map((s) => s.day);
  for (let i = 0; i < dayOrder.length - 1; i++) {
    const today = dayOrder[i];
    const tomorrow = dayOrder[i + 1];
    if (sessionDays.includes(today) && sessionDays.includes(tomorrow)) {
      const todayPatterns = dayPatterns.get(today);
      const tomorrowPatterns = dayPatterns.get(tomorrow);
      if (todayPatterns && tomorrowPatterns) {
        const overlap = [...todayPatterns].filter((p) => tomorrowPatterns.has(p));
        if (overlap.length > 0) {
          const penalty = overlap.length * 2;
          // Distribute penalty to affected muscles
          for (const m of ALL_MUSCLE_GROUPS) {
            muscleFatigue[m] = (muscleFatigue[m] || 0) + penalty * 0.15;
          }
          if (overlap.some((p) => ['squat', 'hinge', 'horizontal_push'].includes(p))) {
            warnings.push({
              severity: 'warning',
              message: `Naładowanie ciężkich ruchów między ${today} a ${tomorrow} (${overlap.map((p) => MOVEMENT_PATTERN_LABELS[p as keyof typeof MOVEMENT_PATTERN_LABELS] || p).join(', ')}) - rozważ rozłożenie tych sesji.`,
            });
          }
        }
      }
    }
  }

  // ── Rest day check ──
  const trainingDays = new Set(week.sessions.map((s) => s.day));
  const restDays = 7 - trainingDays.size;
  if (restDays < MIN_REST_DAYS_PER_WEEK) {
    warnings.push({
      severity: 'critical',
      message: 'Brak dni odpoczynku w tym tygodniu! Regeneracja będzie poważnie zagrożona.',
    });
    for (const m of ALL_MUSCLE_GROUPS) {
      muscleFatigue[m] = (muscleFatigue[m] || 0) * 1.2;
    }
  } else if (restDays < 2) {
    suggestions.push('Rozważ dodanie przynajmniej 2 dni odpoczynku tygodniowo dla optymalnej regeneracji.');
  }

  // ── Calculate overall fatigue (0–100) ──
  const allFatigue = ALL_MUSCLE_GROUPS.map((m) => muscleFatigue[m] || 0);
  const maxMuscleFatigue = Math.max(...allFatigue, 1);
  const avgFatigue = allFatigue.reduce((a, b) => a + b, 0) / allFatigue.length;

  // Normalize: the scale is tuned so that a typical intermediate 4-day program ≈ 40-55
  const rawScore = (avgFatigue * 0.6 + maxMuscleFatigue * 0.4);
  const toleranceScale = 1.0 + (5 - fatigueTolerance) * 0.08; // low tolerance → higher perceived fatigue
  const overallFatigue = Math.min(100, Math.round(rawScore * toleranceScale * 1.8));

  // Normalize per-muscle fatigue to 0–100
  const normFactor = maxMuscleFatigue > 0 ? 100 / (maxMuscleFatigue * 1.2) : 1;
  const normalizedMuscleFatigue: Partial<Record<MuscleGroup, number>> = {};
  for (const m of ALL_MUSCLE_GROUPS) {
    normalizedMuscleFatigue[m] = Math.min(100, Math.round((muscleFatigue[m] || 0) * normFactor));
  }

  // ── Recovery index (inverse of fatigue) ──
  const recoveryIndex = Math.max(0, 100 - overallFatigue);

  // ── Auto-generate suggestions ──
  if (overallFatigue > 75) {
    suggestions.push('Ogólne zmęczenie jest bardzo wysokie - rozważ zmniejszenie objętości lub dodanie tygodnia odciążającego.');
  }
  if (totalHeavyStackingSessions > 1) {
    suggestions.push('Wykryto wiele sesji z nakładającymi się ciężkimi ćwiczeniami złożonymi. Rozłóż intensywność w ciągu tygodnia.');
  }
  if (overallFatigue > 60 && restDays <= 2) {
    suggestions.push('Dodanie dodatkowego dnia odpoczynku może znacząco poprawić regenerację w tym tygodniu.');
  }

  // Per-muscle suggestions
  for (const m of ALL_MUSCLE_GROUPS) {
    if ((normalizedMuscleFatigue[m] || 0) > 80) {
      suggestions.push(
        `Zmęczenie ${MUSCLE_GROUP_LABELS[m]} jest bardzo wysokie (${normalizedMuscleFatigue[m]}%). Rozważ zmniejszenie bezpośredniej objętości lub intensywności.`
      );
    }
  }

  return {
    overallFatigue,
    muscleFatigue: normalizedMuscleFatigue,
    warnings,
    suggestions: [...new Set(suggestions)], // deduplicate
    recoveryIndex,
  };
}
